import React, { FormEvent, useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import InputFeild from "src/Components/InputFeild";
import CircleButton from "src/Components/GlobalComponents/CircleButton";
import httpRequest from "src/helpers/httpRequest";
import { toast } from "react-toastify";
import { useDispatch } from "react-redux";
import { AppDispatch } from "src/Store/store";
import { removeAuth, setAuth, setUserDetails } from "src/Store/reducer";
import { jwtDecode } from "jwt-decode";
import Cookies from "js-cookie";
import {
  decodedToken,
  getLoginUserRole,
  getToken,
  removeToken,
} from "src/helpers/helperFunctions";
import { doctor, doctorMode } from "src/lib/constans";
import { setDoctorState } from "src/Store/doctor";
import { removeAllIndicators } from "src/Store/redDots";
import moment from "moment";
import SplashScreen from "src/Components/SplashScreen";

interface ItokenReceived {
  token: string;
  message: string;
  [key: string]: any;
}

interface IPayloadState {
  email: string;
  password: string;
  keepmeLogIn: boolean;
  userRole: string;
}

const initialState: IPayloadState = {
  email: "",
  password: "",
  keepmeLogIn: false,
  userRole: "doctor",
};

const initialErroState = {
  email: "",
  password: "",
};

const DoctorLogin: React.FC = () => {
  const navigate = useNavigate();
  const dispatch = useDispatch<AppDispatch>();
  const token = getToken();
  const [isLoader, setIsLoader] = useState<boolean>(false);
  const [errorField, setErrorField] = useState(initialErroState);
  const [payload, setPayload] = useState<IPayloadState>(initialState);
  const [pageLoader, setPageLoader] = useState<boolean>(false);

  const handleChange = (key: keyof IPayloadState, val: string) => {
    setPayload((prev) => ({ ...prev, [key]: val }));
    if (val) {
      setErrorField((prev) => ({ ...prev, [key]: "" }));
    }
  };

  const validateFields = () => {
    const errors: typeof errorField = {
      email: payload?.email ? "" : "Email is required.",
      password: payload.password ? "" : "Password is required.",
    };
    setErrorField(errors);
    // Check if there are any errors
    return !Object.values(errors).some((error) => error !== "");
  };

  const fetchProfile = async (_token: string , showToast = true) => {
    const { res, err } = await httpRequest<any>({
      path: `/doctors/profile/${decodedToken(_token)?.id}`,
      token: _token,
    });
    if (res) {
      dispatch(setUserDetails(res));
      // Always set doctor state since this is doctor login
      dispatch(setDoctorState(res?._id));
      dispatch(setAuth(_token));
      Cookies.set(doctorMode, doctor);
      if (showToast) {
        toast("Login successfully", { type: "success" });
      }
      navigate("/dashboard");
      setPageLoader(false);
    } else {
      toast(err, { type: "error" });
      dispatch(removeAuth()); // logout
      dispatch(removeAllIndicators());
      removeToken();
      navigate("/doctor/login");
      setPageLoader(false);
    }
  };

  const handleLogin = async (event: FormEvent) => {
    event.preventDefault();
    if (validateFields()) {
      setIsLoader(true);
      const { res, err } = await httpRequest<ItokenReceived>({
        method: "post",
        path: "/customer/login",
        params: { ...payload, userRole: "doctor" },
      });
      if (res) {
        if (res?.otpExpire) {
          let obj = {
            email: payload.email,
            otpExpire: moment().add(2, "minutes").toISOString(),
            purpose: "change-password",
          };
          sessionStorage.setItem(
            "doctorTwoFactor" as string,
            JSON.stringify(obj)
          );
          navigate("/doctor/otp");
          return;
        }
        let { message = "Success!", token = "" } = res;
        fetchProfile(token);
        setErrorField(initialErroState);
        setPayload(initialState);
      } else {
        toast(err, { type: "error" });
      }
      setIsLoader(false);
    }
  };

  useEffect(() => {
    if (token && getLoginUserRole() === "doctor") {
      setPageLoader(true);
      fetchProfile(token, false);
    }
  }, [token]);

  return (
    <div className="customer-login-main">
      {pageLoader && <SplashScreen />}
      <div className="container-1600">
        <div className="customer-login-main-inner">
          <div>
            <h1 className="font-Poppins-SemiBold font-size-32px theme-color-green leading-none text-center lg-main-head">
              Welcome Doctor!
            </h1>
            <p className="font-size-16px theme-color-green font-Poppins-Regular text-center">
              To login please enter your email
            </p>
          </div>
          <form style={{ width: "100%" }} onSubmit={handleLogin}>
            <div className="customer-login-main-inner-main">
              <div>
                <InputFeild
                  id="email"
                  label="Email"
                  value={payload.email}
                  onChange={(val: string) => handleChange("email", val)}
                />
                {errorField?.email && (
                  <p className="font-size-14px text-[#ff0000] font-Poppins-Regular">
                    {errorField?.email}
                  </p>
                )}
              </div>
              <div>
                <InputFeild
                  id="password"
                  type="password"
                  label="Password"
                  value={payload.password}
                  onChange={(val: string) => handleChange("password", val)}
                />
                {errorField?.password && (
                  <p className="font-size-14px text-[#ff0000] font-Poppins-Regular">
                    {errorField?.password}
                  </p>
                )}
              </div>
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-2">
                  <input
                    id="keep-me-logged-in"
                    className="input-checkbox"
                    type="checkbox"
                    checked={payload.keepmeLogIn}
                    onChange={(e) =>
                      setPayload((prev) => ({
                        ...prev,
                        keepmeLogIn: !prev.keepmeLogIn,
                      }))
                    }
                  />
                  <label
                    htmlFor="keep-me-logged-in"
                    className="font-size-13px font-Poppins-Medium text-white inline-block cursor-pointer"
                  >
                    Keep me logged in
                  </label>
                </div>
                <Link
                  id="forgetPasswordLink"
                  className="font-size-13px font-Poppins-Medium text-white underline hover:no-underline"
                  to="/doctor/forget-password"
                >
                  Forget Password?
                </Link>
              </div>
              <div className="login-btn-home-banner-parent flex items-center justify-center">
                <CircleButton
                  id="submit_btn"
                  ButtonText={"Login"}
                  loader={isLoader}
                  type={"submit"}
                  // btnExtralClasses={
                  //   "border border-[#00443f] hover:bg-[#fff]  hover:text-[#00443f]"
                  // }
                />
              </div>
            </div>
          </form>
          <p className="font-size-16px theme-color-green font-Poppins-Regular">
            Don't have an account?{" "}
            <Link
              to="/doctor/register"
              id="signUpLink"
              className="font-Poppins-SemiBold underline hover:no-underline"
            >
              Sign Up
            </Link>
          </p>
        </div>
      </div>
    </div>
  );
};

export default DoctorLogin;
